import * as React from 'react';
import { Helmet } from 'react-helmet';

import { type ImgParams, getOptimizedSrcs } from 'js/lib/imgix';

export type ResponsiveImageType = {
  /**
   * The id of the image.
   */
  id?: string | null;
  /**
   * the URL of the image
   */
  url?: string | null;
  /**
   * The width of the rendered image in pixels.
   * If not provided, image will be loaded at its original width.
   */
  width?: number | null;
  /**
   * The height of the rendered image in pixels.
   * If not provided, image will be loaded at its original height.
   */
  height?: number | null;
  /**
   * Image alt text used for accessibility and assigned to the alt attribute.
   */
  alt?: string | null;
  /**
   * The media query string for the image.
   * This is a required prop and is used to assign to the media attribute of the <source> element,
   * to determine which image to load for a given viewport to avoid loading more than one image per viewport.
   */
  media: string;
  /**
   * Whether to use this image as a fallback when none of the <source> elements provide a usable image.
   */
  isFallback?: boolean;
};

type Props = {
  images: ResponsiveImageType[];
  fetchPriority: 'high' | 'auto' | 'low';
  imgParams?: ImgParams;
};

const DEFAULT_IMG_PARAMS = {
  quality: 40,
  fit: 'clip',
  auto: 'format, compress',
};

const getImageSrcData = (image: ResponsiveImageType, imgParams?: ImgParams): { src: string; srcSet: string } | null => {
  const imgParamsToUse = { ...DEFAULT_IMG_PARAMS, ...imgParams };

  if (!image.url) {
    return null;
  }

  const width = image.width ?? undefined;
  const height = image.height ?? undefined;

  const srcs = getOptimizedSrcs(image.url, width, height, imgParamsToUse);

  if (!srcs.dpr1) {
    return null;
  }

  const srcSet = srcs.dpr2 && srcs.dpr3 ? `${srcs.dpr1} 1x, ${srcs.dpr2} 2x, ${srcs.dpr3} 3x` : `${srcs.dpr1} 1x`;

  return {
    src: srcs.dpr1,
    srcSet,
  };
};

/**
 * Renders a responsive image with the given images and fetch priority.
 * @param images - The images to render.
 * @param fetchPriority - The fetch priority to use for the image.
 * @param imgParams - Optional imgix parameters to customize image optimization (e.g., quality, fit).
 * These params are merged with DEFAULT_IMG_PARAMS, allowing override of defaults.
 * @returns A <picture> element with <source> elements for each image and a <img> element for the fallback image.
 * If fetchPriority is 'high', a <Helmet> element is also returned, which renders <link> elements
 * to preload the images based on the media queries to improve the loading performance.
 * @example
 * <ResponsiveImage
 *   images={[
 *     { url: 'https://example.com/mobile-image.jpg', width: 100, height: 100, media: '(max-width: 768px)' },
 *     { url: 'https://example.com/desktop-image.jpg', width: 200, height: 200, media: '(min-width: 769px)', isFallback: true },
 *   ]}
 *   fetchPriority="high"
 * />
 */
export const ResponsiveImage = ({ images, fetchPriority, imgParams }: Props) => {
  const { imageElement, sourceElements, helmetLinks } = React.useMemo(() => {
    let imgElement = null;
    const srcElements: React.ReactElement[] = [];
    const preloadLinks: React.JSX.IntrinsicElements['link'][] = [];

    images.forEach((image) => {
      const srcData = getImageSrcData(image, imgParams);
      if (!srcData) {
        return;
      }

      srcElements.push(<source srcSet={srcData.srcSet} media={image.media} key={image.id} />);

      if (image.isFallback) {
        imgElement = (
          <img
            src={srcData.src}
            srcSet={srcData.srcSet}
            fetchPriority={fetchPriority}
            alt={image.alt ?? ''}
            key={image.id}
          />
        );
      }

      preloadLinks.push({
        rel: 'preload',
        as: 'image',
        href: srcData.src,
        imageSrcSet: srcData.srcSet,
        fetchPriority,
        media: image.media,
      });
    });

    return {
      imageElement: imgElement,
      sourceElements: srcElements,
      helmetLinks: preloadLinks,
    };
  }, [images, fetchPriority, imgParams]);

  if (!imageElement || !sourceElements.length) {
    return null;
  }

  const imageOutput = (
    <picture>
      {sourceElements}
      {imageElement}
    </picture>
  );

  if (fetchPriority !== 'high') {
    return imageOutput;
  }

  return (
    <>
      <Helmet link={helmetLinks} />
      {imageOutput}
    </>
  );
};

export default ResponsiveImage;
